import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EndpointService } from '@app/core/http/endpoint.service';
import { Logger } from '@app/core/utilities/logger.service';
import { UsuarioActual, UserToken, Permiso } from '@app/shared';
import { environment } from '@env/environment';
import * as jwtDecode from 'jwt-decode';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';
import { secretbox, randomBytes } from 'tweetnacl';
import { find } from 'lodash';
import {
  encodeUTF8,
  decodeBase64
} from 'tweetnacl-util';

const log = new Logger('AuthenticationService');

export interface Credentials {
  token: string;
  permissions: string;
  changePassword?: boolean
}

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}

// Nombre de la llave para recuperar el token del almacenamiento local
const credentialsKey = environment.credentialsKey;

@Injectable()
export class AuthenticationService {
  // Token en estándar JWT
  private token: string = null;
  // Permisos del usuario en sesion
  private permissions: string = null;
  // Datos del usuario sacados del token
  private user: UserToken = null;
  // Define si el usuario debe cambiar la contraseña
  userChangePassword: boolean = false;

  constructor(
    private api: EndpointService,
    private lss: LocalStorageService,
    private sss: SessionStorageService,
    private http: HttpClient,
    private toastr: ToastrService,
  ) {
    // Recuperar credenciales del almacenamiento local
    this.getCredentials(true);
  }

  /**
   * Recupera el token(JWT) y autentica al usuario.
   *
   * @param {LoginContext} context Parámetros de inicio de sesión.
   * @return {Observable<UserToken>} Credenciales del usuario.
   */
  login(context: LoginContext): Observable<UserToken> {
    return new Observable(observer => {
      this.http
        .post<Credentials>(
          this.api.get('login'),
          context
        )
        .subscribe(
          data => {
            this.setCredentials(data, context.remember);
            this.userChangePassword = data.changePassword;
            observer.next({user: this.user, changePassword: data.changePassword});
          },
          error => {
            observer.error(error);
          }
        );
    });
  }

  /**
   * Refresca el token
   *
   * @param {string} token
   * @returns {Observable<Credentials>}
   * @memberof AuthenticationService
   */
  refreshToken(token: string): Observable<Credentials> {
    const formData = {
      token: token
    };
    return new Observable(observer => {
      this.http
        .post<Credentials>(
          this.api.get('refresToken'),
          formData
        )
        .subscribe(
          data => {
            const credentialsFromLs: Credentials = this.lss.retrieve(credentialsKey);
            this.setCredentials(data, credentialsFromLs ? true : false);
            observer.next(data);
          },
          error => {
            this.toastr.warning(
              'Debido a el tiempo de inhabilidad en el sistema la sesión ha sido expirada',
              'Sesión expirada'
            );
            this.logout();
            location.reload();
            observer.error(error);
          }
        );
    });
  }

  /**
   * Desconecta al usuario y borra las credenciales.
   *
   * @return {boolean>} True si el usuario cerró la sesión con éxito.
   */
  logout(): boolean {
    this.setCredentials();
    return true;
  }

  /**
   * Decodifica el token usando jwtDecode.
   *
   * @param {string} [token]
   * @returns {UserToken}
   * @memberof AuthenticationService
   */
  getDecodeToken(token?: string): UserToken {
    const _token = token ? token : this.token;
    if (_token) {
      return jwtDecode<UserToken>(_token);
    }
    return null;
  }

  /**
   * Decodifica el token(JWT) y obtiene la fecha de vencimiento.
   *
   * @param {string} token
   * @returns {Date}
   */
  getTokenExpirationDate(token: string): Date {
    const decoded = this.getDecodeToken(token);
    if (decoded.exp === undefined) {
      return null;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date || null;
  }

  /**
   * Valida si la fecha de vencimiento del token ha expirado.
   *
   * @param {string} token
   * @returns {boolean} True si la fecha ha expirado.
   */
  isTokenExpired(token?: string): boolean {
    const _token = token ? token : this.token;
    if (!_token) {
      return true;
    }
    const date = this.getTokenExpirationDate(_token);
    return date && !(date.valueOf() > new Date().valueOf());
  }

  /**
   * Verifica que si el  usuario está autenticado.
   *
   * @return {boolean} True si el usuario está autenticado.
   */
  isAuthenticated(): boolean {
    return !!(this.getCredentials() && !this.isTokenExpired());
  }

  /**
   * Obtiene las credenciales del usuario.
   *
   * @return {UsuarioActual} - Datos del usuario o null si este no esta autenticado.
   */
  getCredentials(forceRefresh: boolean = false): UsuarioActual {
    if (forceRefresh) {
      // Refrescar token
      this.token = null;
      this.permissions = null;
      this.user = null;
      // SesionStorage
      const credentialsFromSs: Credentials = this.sss.retrieve(credentialsKey);
      // LocalStorage
      const credentialsFromLs: Credentials = this.lss.retrieve(credentialsKey);
      if (credentialsFromSs || credentialsFromLs) {
        this.setCredentials(
          credentialsFromSs || credentialsFromLs,
          credentialsFromLs ? true : false
        );
      }
    }
    let user_data: UsuarioActual;
    if (this.user && this.user.user_data) {
      user_data = this.decrypt(this.user.user_data, environment.securityKey) as UsuarioActual;
      user_data.permisos = this.decrypt(this.permissions, environment.securityKey) as Permiso[];
    }
    return user_data;
  }

  /**
   * Establece las credenciales del usuario.
   * Las credenciales pueden persistir en todas las sesiones al establecer el parámetro `remember` en true.
   * De lo contrario, las credenciales solo se conservan para la sesión actual.
   *
   * @param {Credentials=} credentials Las credenciales de usuario.
   * @param {boolean=} remember True para recordar las credenciales en todas las sesiones.
   */
  private setCredentials(credentials?: Credentials, remember?: boolean) {
    const storage = remember ? this.lss : this.sss;
    if (credentials) {
      this.token = credentials.token;
      this.permissions = credentials.permissions;
      // Decodifica el token y saca la información del usuario
      this.user = this.getDecodeToken(credentials.token);
      // Guardar credenciales en el almacenamiento local
      storage.store(credentialsKey, credentials);
    } else {
      // Eliminar credenciales del almacenamiento local
      this.lss.clear(credentialsKey);
      this.sss.clear(credentialsKey);
    }
  }

  /**
   * Desencrita la informacion del usuario traida en el token
   *
   * @param {*} messageWithNonce
   * @param {*} key
   * @returns {UsuarioActual}
   * @memberof AuthenticationService
   */
  decrypt(messageWithNonce: any, key: any): Object {
    // const keyUint8Array = decodeBase64(randomBytes(key));
    if (messageWithNonce) {
      const keyUint8Array = Buffer.from(key, 'utf8');
      const messageWithNonceAsUint8Array = decodeBase64(messageWithNonce);
      const nonce = messageWithNonceAsUint8Array.slice(0, secretbox.nonceLength);
      const message = messageWithNonceAsUint8Array.slice(
        secretbox.nonceLength,
        messageWithNonce.length
      );
      const decrypted = secretbox.open(message, nonce, keyUint8Array);
      if (!decrypted) {
        throw new Error('Could not decrypt message');
      }
      const base64DecryptedMessage = encodeUTF8(decrypted);
      return JSON.parse(atob(base64DecryptedMessage));
    } else {
      return null;
    }
  }

  /**
   * Consulta si el usuario en sesión tiene un tipo de permiso o un permiso en especifico
   *
   * @param {string} typePermission
   * @param {string} [actionPermission]
   * @returns
   * @memberof AuthenticationService
   */
  confirmPermission(typePermission: string, actionPermission?: string) {
    let activateAccess = false;
    const credentials = this.getCredentials();
    if (credentials) {
      if (credentials.permisos && credentials.permisos.length) {
        const permission = find(credentials.permisos, {permiso: typePermission});
        if (permission) {
          if (!actionPermission) {
            activateAccess = true;
          } else {
            if (permission.acciones.length) {
              const action = find(permission.acciones, {nombre: actionPermission});
              if (action) {
                activateAccess = true;
              }
            }
          }
        }
      }
    }

    return activateAccess;
  }

  /**
   *Cambio de contraseña de usuario
   *
   * @param {string} currentPassword
   * @param {string} newPassword
   * @return {*}  {Observable<boolean>}
   * @memberof AuthenticationService
   */
  changePassword(currentPassword: string, newPassword: string): Observable<boolean> {
    return new Observable(obs => {
      const dataForm = {
        password: currentPassword,
        new_password: newPassword
      };
      this.http.skipErrorHandler().post(this.api.get('changeUserPassword'), dataForm).subscribe(
        res => {
          if (res['message']) {
            this.toastr.success(res['message'], 'Operación exitosa');
          } else {
            this.toastr.success('Contraseña actualizada correctamente', 'Operación exitosa');
          }
          obs.next(true);
        },
        err => {
          if (err.status !== 401 && err.status !== 403) {
            if (err.error.message) {
              this.toastr.error(err.error.message, 'Error de operación');
            } else if (typeof err.error === 'string') {
              this.toastr.error(err.error, 'Error de operación');
            } else {
              this.toastr.error('Lo sentimos ha ocurrido un error', 'Error de operación');
            }
          }
          obs.error(err);
        }
      );
    });
  }

  /**
   *Define el cambio de contraseña obligatorio para el usuario
   *
   * @return {boolean} {userChangePassword}
   * @memberof AuthenticationService
   */
  getChangePassword(){
    return this.userChangePassword;
  }

}



// WEBPACK FOOTER //
// ./src/app/core/authentication/authentication.service.ts